import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { fmtINR, fmtLakh, fmtDate, BUCKET_META } from "../lib/format";
import CircuitTable from "../components/CircuitTable";
import RechargeModal from "../components/RechargeModal";
import CancelModal from "../components/CancelModal";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, CheckCircle2, Database, CalendarDays, ArrowRight, ShieldCheck } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [kpis, setKpis] = useState([]);
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState("overdue");
  const [svc, setSvc] = useState("");
  const [recharge, setRecharge] = useState(null);
  const [cancel, setCancel] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [sync, setSync] = useState(null);
  const [forecast, setForecast] = useState(null);

  const load = useCallback(async () => {
    const [k, s] = await Promise.all([
      api.get(`/dashboard/kpis${svc ? `?service_type=${svc}` : ""}`),
      api.get(`/dashboard/summary`),
    ]);
    setKpis(k.data.buckets);
    setSummary(s.data);
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/sync/lastmile/report").then((r) => setSync(r.data)).catch(() => {});
    api.get("/dashboard/forecast").then((r) => setForecast(r.data)).catch(() => {});
  }, [svc]);

  useEffect(() => { load(); }, [load, refresh]);

  const totalOverdueToday = kpis.filter((b) => ["overdue", "today"].includes(b.key)).reduce((a, b) => a + b.count, 0);
  const done = () => { setRefresh((r) => r + 1); };

  const dqLabels = { expiry_missing: "Expiry Missing", cycle_missing: "Cycle Missing", amount_missing: "Amount Missing", isp_id_missing: "ISP ID Missing", assigned_missing: "Unassigned", location_missing: "No Location" };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold text-slate-900">Broadband Renewal Control</h1>
          <p className="text-slate-500 text-sm mt-1">Good morning, {user?.name?.split(" ")[0]} — here is everything that needs action today.</p>
        </div>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg text-sm">
          {[["", "All"], ["Broadband", "Broadband"], ["ILL", "ILL"]].map(([v, l]) => (
            <button key={v} onClick={() => setSvc(v)} data-testid={`svc-filter-${l}`}
              className={`px-3 py-1.5 rounded-md font-medium ${svc === v ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {totalOverdueToday > 0 && (
        <div className="flex items-center gap-3 bg-red-600 text-white rounded-xl px-4 py-3 shadow-sm" data-testid="urgency-banner">
          <AlertTriangle className="shrink-0" />
          <div className="text-sm font-medium">
            <b>{totalOverdueToday} connection(s)</b> are overdue or expiring today. Recharge them now to prevent disconnection.
          </div>
        </div>
      )}

      {/* Critical-failure alerts (backup / offsite / AI key / sync / spend) */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="alerts-banner">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><AlertTriangle size={16} /> {alerts.length} active alert(s)</div>
          <ul className="mt-1 space-y-0.5">
            {alerts.slice(0, 6).map((a) => (
              <li key={a.id} className="text-sm text-amber-800"><b className="uppercase text-[10px] mr-1">{a.code}</b>{a.message}{a.count > 1 ? ` (×${a.count})` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {/* LastMile sync + 90-day cash-out forecast */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><Database size={16} /> LastMile sync</div>
          {sync?.ran_at ? (
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div>Last run {fmtDate(sync.ran_at)} · {sync.ok ? <span className="text-emerald-600">OK</span> : <span className="text-red-600">failed</span>}</div>
              <div className="text-xs text-slate-500">created {sync.created || 0} · updated {sync.updated || 0} · suspended {sync.suspended || 0}{(sync.errors || []).length ? ` · ${sync.errors.length} error(s)` : ""}</div>
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">No sync has run yet.</div>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 font-head font-bold text-sm text-slate-800"><CalendarDays size={16} /> 90-day cash-out</div>
          {forecast ? (
            <div className="mt-2 text-sm text-slate-700 space-y-1">
              <div>Total expected outflow: <b>{fmtLakh(forecast.total)}</b> across {forecast.by_vendor?.length || 0} vendor(s)</div>
              <div className="text-xs text-slate-500">{(forecast.by_vendor || []).slice(0, 4).map((v) => `${v.vendor}: ${fmtINR(v.amount)}`).join(" · ")}</div>
              {forecast.cycle_change_bumps?.length > 0 && <div className="text-xs text-amber-700">⚑ {forecast.cycle_change_bumps.length} cycle-change bump(s) in the window</div>}
            </div>
          ) : <div className="mt-2 text-sm text-slate-400">No forecast yet.</div>}
        </div>
      </div>

      {/* Renewal Coverage KPI */}
      {summary?.renewal_coverage && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="coverage-kpi">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-6">
            <div className="relative shrink-0">
              <svg width="96" height="96" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="40" fill="none" stroke="#E2E8F0" strokeWidth="10" />
                <circle cx="48" cy="48" r="40" fill="none" stroke={summary.renewal_coverage.broadband_coverage_pct >= 98 ? "#16A34A" : summary.renewal_coverage.broadband_coverage_pct >= 90 ? "#2563EB" : "#EA580C"}
                  strokeWidth="10" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - summary.renewal_coverage.broadband_coverage_pct / 100)}`}
                  transform="rotate(-90 48 48)" style={{ transition: "stroke-dashoffset .6s ease" }} />
                <text x="48" y="52" textAnchor="middle" className="font-head" fontSize="20" fontWeight="800" fill="#0F172A">{summary.renewal_coverage.broadband_coverage_pct}%</text>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-800 font-head font-bold"><ShieldCheck size={18} className="text-emerald-600" /> Renewal Coverage — Broadband</div>
              <p className="text-sm text-slate-500 mt-1">Active broadband circuits under automatic renewal control.</p>
              <div className="flex gap-6 mt-3 text-sm">
                <div><div className="font-head text-2xl font-extrabold">{summary.renewal_coverage.broadband_active}</div><div className="text-xs text-slate-500">Active Broadband</div></div>
                <div><div className="font-head text-2xl font-extrabold text-emerald-600">{summary.renewal_coverage.broadband_controlled}</div><div className="text-xs text-slate-500">Under Control</div></div>
                <div><div className="font-head text-2xl font-extrabold text-orange-600">{summary.renewal_coverage.broadband_incomplete}</div><div className="text-xs text-slate-500">Incomplete</div></div>
              </div>
            </div>
          </div>
          <button onClick={() => nav("/data-quality?view=incomplete")} data-testid="renewal-incomplete-exception"
            className={`text-left rounded-xl border p-5 transition-colors ${summary.renewal_coverage.incomplete > 0 ? "bg-orange-50 border-orange-300 hover:border-orange-400" : "bg-emerald-50 border-emerald-300"}`}>
            <div className="flex items-center gap-2 font-head font-bold text-orange-700"><AlertTriangle size={18} /> Renewal Data Incomplete</div>
            <div className="font-head text-4xl font-extrabold text-orange-600 mt-3">{summary.renewal_coverage.incomplete}</div>
            <p className="text-sm text-slate-600 mt-1">Active circuits NOT under renewal control. Click to fix them →</p>
          </button>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {kpis.map((b) => {
          const meta = BUCKET_META[b.key];
          return (
            <button key={b.key} data-testid={`kpi-card-${b.key}`} onClick={() => setActive(b.key)}
              className={`kpi-card text-left rounded-xl border p-4 ${meta.card} ${active === b.key ? "active" : ""}`}>
              <div className={`text-[11px] font-bold uppercase tracking-wide ${meta.text}`}>{meta.label}</div>
              <div className={`font-head text-3xl font-extrabold mt-2 ${meta.num}`}>{b.count}</div>
              <div className="text-xs text-slate-500 mt-0.5">connections</div>
              <div className={`font-mono-num text-sm font-semibold mt-2 ${meta.text}`}>{fmtINR(b.amount)}</div>
            </button>
          );
        })}
      </div>

      {/* Action Required table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-head text-lg font-bold flex items-center gap-2">
            Action Required
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${BUCKET_META[active]?.badge}`}>{BUCKET_META[active]?.label}</span>
          </h2>
          <button onClick={() => nav("/action-required")} className="text-sm text-blue-700 font-medium flex items-center gap-1 hover:gap-2 transition-all">Full view <ArrowRight size={15} /></button>
        </div>
        <CircuitTable baseFilters={{ bucket: active, service_type: svc, status: "Active" }} onRecharge={setRecharge} onCancel={setCancel} refreshKey={refresh} />
      </div>

      {/* Secondary row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <button onClick={() => nav("/action-required?view=verify")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-emerald-300 transition-colors" data-testid="tile-verification">
          <div className="flex items-center gap-2 text-emerald-600"><CheckCircle2 size={18} /><span className="font-head font-bold">Recharge Verification Pending</span></div>
          <div className="font-head text-3xl font-extrabold mt-3">{summary?.verification_pending ?? "—"}</div>
          <p className="text-sm text-slate-500 mt-1">Recharged circuits awaiting internet/payment confirmation.</p>
        </button>
        <button onClick={() => nav("/calendar")} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 transition-colors" data-testid="tile-calendar">
          <div className="flex items-center gap-2 text-blue-600"><CalendarDays size={18} /><span className="font-head font-bold">Upcoming Renewal Calendar</span></div>
          <div className="font-head text-3xl font-extrabold mt-3">{kpis.reduce((a, b) => a + b.count, 0)}</div>
          <p className="text-sm text-slate-500 mt-1">Renewals due in the next 30 days. Open the calendar to plan cash.</p>
        </button>
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="tile-dataquality">
          <div className="flex items-center gap-2 text-orange-600"><Database size={18} /><span className="font-head font-bold">Missing Information</span></div>
          <div className="flex flex-wrap gap-2 mt-3">
            {summary && Object.entries(summary.data_quality || {}).map(([k, v]) => (
              <button key={k} onClick={() => nav(`/data-quality?flag=${k}`)} className="text-xs px-2.5 py-1.5 rounded-lg bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100">
                <b>{v}</b> {dqLabels[k] || k}
              </button>
            ))}
            {summary && Object.keys(summary.data_quality || {}).length === 0 && <span className="text-sm text-slate-400">All data complete 🎉</span>}
          </div>
        </div>
      </div>

      {recharge && <RechargeModal circuit={recharge} onClose={() => setRecharge(null)} onDone={done} />}
      {cancel && <CancelModal circuit={cancel} onClose={() => setCancel(null)} onDone={done} />}
    </div>
  );
}
